import React, { useState, useEffect } from 'react';
import Badge from '@/components/common/Badge';
import Card, { CardBody } from '@/components/common/Card';
import ConfirmDialog, { DeleteIcon } from '@/components/common/ConfirmDialog';
import { FaUserAlt, FaCalendarAlt, FaEnvelope, FaPhone, FaTools, FaPen, FaTrash, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { normalizeDataObject } from '../../utils/normalizationUtils';

/**
 * Componente para mostrar un mecánico individual
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.mecanico - Datos del mecánico
 * @param {Array} props.sucursales - Lista de sucursales disponibles
 * @param {Array} props.especialidades - Lista de especialidades disponibles
 * @param {function} props.onEdit - Función para editar el mecánico
 * @param {function} props.onSchedule - Función para gestionar horario
 * @param {function} props.onToggleActive - Función para activar/desactivar
 * @param {function} props.onDelete - Función para eliminar el mecánico
 * @returns {JSX.Element} - Componente de ítem de mecánico
 */
const MecanicoItem = ({ 
  mecanico, 
  sucursales = [],
  especialidades = [],
  onEdit, 
  onSchedule, 
  onToggleActive, 
  onDelete
}) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Normalizar datos del mecánico para manejar cualquier formato
  const normalizedMecanico = normalizeDataObject(mecanico, 'mecanico');
  
  // Helper para estado activo
  const isActive = normalizedMecanico.activo !== false;
  
  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Comprobar inicialmente
    checkMobile();
    
    // Actualizar cuando cambie el tamaño de la ventana
    window.addEventListener('resize', checkMobile);
    
    // Limpiar al desmontar
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Función para obtener el nombre de la especialidad por ID
  const getEspecialidadNombre = () => {
    if (!normalizedMecanico.categoriaId || normalizedMecanico.categoria_id) return null;
    const especialidad = especialidades.find(esp => esp.id === normalizedMecanico.categoriaId || normalizedMecanico.categoria_id);
    return especialidad ? especialidad.nombre : null;
  };
  
  // Función para obtener el color de borde según la especialidad
  const getBorderColor = () => {
    const especialidadNombre = getEspecialidadNombre();
    switch (especialidadNombre) {
      case 'Motor':
        return 'border-red-500';
      case 'Frenos':
        return 'border-yellow-500';
      case 'Suspensión':
        return 'border-blue-500';
      case 'Eléctrico':
        return 'border-purple-500';
      case 'Diagnóstico':
        return 'border-green-500';
      case 'Transmisión':
        return 'border-orange-500';
      default:
        return 'border-gray-500';
    }
  };
  
  // Obtener nombre de la sucursal
  const getSucursalNombre = () => {
    const sucursalId = Array.isArray(normalizedMecanico.sucursal || normalizedMecanico.Sucursal) ? normalizedMecanico.sucursal || normalizedMecanico.Sucursal[0] : normalizedMecanico.sucursal || normalizedMecanico.Sucursal;
    const sucursal = sucursales.find(s => s.id === sucursalId);
    return sucursal ? sucursal.Nombre : 'Sin asignar';
  };
  
  // Determinar estado activo para el badge
  const getEstadoBadge = () => {
    return isActive ? 
      { label: 'Activo', variant: 'success' } : 
      { label: 'Inactivo', variant: 'danger' };
  };
  
  const estadoBadge = getEstadoBadge();
  
  return (
    <>
      <Card 
        hover 
        className={`mb-3 border-l-4 ${getBorderColor()} transition-all duration-300`}
      >
        <CardBody className="p-0">
          {isMobile ? (
            // Versión móvil
            <div>
              <div className="p-4">
                {/* Nombre y estado */}
                <div className="flex items-center mb-3">
                  <h3 className="text-lg font-semibold">{normalizedMecanico.nombre || normalizedMecanico.Nombre}</h3>
                  <Badge 
                    variant={estadoBadge.variant} 
                    size="sm"
                    className="ml-2"
                  >
                    {estadoBadge.label}
                  </Badge>
                </div>
                
                {/* Información del mecánico */}
                <div className="text-sm">
                  <div className="flex items-center mb-2">
                    <FaTools className="mr-2 text-gray-500" />
                    <span className="text-gray-300 mr-2">Especialidad:</span>
                    <Badge variant="info" size="sm">
                      {getEspecialidadNombre() || 'Sin especialidad'}
                    </Badge>
                  </div>
                  
                  
                  {normalizedMecanico.email || normalizedMecanico.Email && (
                    <div className="flex items-center mb-2">
                      <FaEnvelope className="mr-2 text-gray-500" />
                      <span className="text-gray-300">
                        {normalizedMecanico.email || normalizedMecanico.Email}
                      </span>
                    </div>
                  )}
                  
                  {normalizedMecanico.telefono || normalizedMecanico.Telefono && (
                    <div className="flex items-center">
                      <FaPhone className="mr-2 text-gray-500" />
                      <span className="text-gray-300">
                        {normalizedMecanico.telefono || normalizedMecanico.Telefono}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Botones móviles */}
              <div className="flex justify-between border-t border-gray-700 bg-gray-800">
                <button
                  onClick={() => onEdit(mecanico)}
                  className="flex-1 py-3 flex flex-col items-center justify-center text-yellow-500"
                  title="Editar"
                >
                  <FaPen className="text-lg" />
                  <span className="text-xs mt-1">Editar</span>
                </button>
                
                {/* BOTÓN HORARIO COMENTADO - FUNCIONALIDAD FUTURA */}
                {/* <button
                  onClick={() => onSchedule(mecanico)}
                  className="flex-1 py-3 flex flex-col items-center justify-center text-blue-500"
                  title="Horario"
                >
                  <FaCalendarAlt className="text-lg" />
                  <span className="text-xs mt-1">Horario</span>
                </button> */}
                
                <button
                  onClick={() => onToggleActive(normalizedMecanico.id || mecanico.id, !isActive)}
                  className={`flex-1 py-3 flex flex-col items-center justify-center ${isActive ? 'text-green-500' : 'text-red-500'}`}
                  title={isActive ? 'Desactivar' : 'Activar'}
                >
                  {isActive ? <FaToggleOn className="text-lg" /> : <FaToggleOff className="text-lg" />}
                  <span className="text-xs mt-1">{isActive ? 'Desactivar' : 'Activar'}</span>
                </button>
                
                <button
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="flex-1 py-3 flex flex-col items-center justify-center text-red-500"
                  title="Eliminar"
                >
                  <FaTrash className="text-lg" />
                  <span className="text-xs mt-1">Eliminar</span>
                </button>
              </div>
            </div>
          ) : (
            // Versión escritorio
            <div className="flex justify-between items-center p-4">
              {/* Información del mecánico organizada en grid */}
              <div className="flex-grow grid grid-cols-12 gap-4 items-center">
                {/* Columna 1: Ícono y nombre */}
                <div className="col-span-6 flex items-center">
                  <div className="mr-3 bg-blue-600 bg-opacity-20 p-2 rounded-full">
                    <FaUserAlt className="text-blue-400" />
                  </div>
                  <div className="flex flex-col">
                    <h3 className="text-lg font-semibold">{normalizedMecanico.nombre || normalizedMecanico.Nombre}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      {getEspecialidadNombre() && (
                        <Badge variant="info" size="xs">{getEspecialidadNombre()}</Badge>
                      )}
                      <Badge
                        variant={estadoBadge.variant}
                        size="xs"
                      >
                        {estadoBadge.label}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                
                {/* Columna 2: Email */}
                <div className="col-span-3 text-sm">
                  {normalizedMecanico.email || normalizedMecanico.Email && (
                    <div className="flex items-center">
                      <FaEnvelope className="mr-2 text-gray-500" />
                      <span className="text-gray-300 truncate">{normalizedMecanico.email || normalizedMecanico.Email}</span>
                    </div>
                  )}
                </div>
                
                {/* Columna 3: Teléfono */}
                <div className="col-span-3 text-sm">
                  {normalizedMecanico.telefono || normalizedMecanico.Telefono && (
                    <div className="flex items-center">
                      <FaPhone className="mr-2 text-gray-500" />
                      <span className="text-gray-300">{normalizedMecanico.telefono || normalizedMecanico.Telefono}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Botones de acción */}
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => onEdit(mecanico)}
                  className="text-white bg-yellow-600 hover:bg-yellow-700 px-2 py-1 rounded"
                  title="Editar"
                >
                  <FaPen />
                </button>
                
                {/* BOTÓN HORARIO COMENTADO - FUNCIONALIDAD FUTURA */}
                {/* <button
                  onClick={() => onSchedule(mecanico)}
                  className="text-white bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded"
                  title="Gestionar Horario"
                >
                  <FaCalendarAlt />
                </button> */}
                
                <button
                  onClick={() => onToggleActive(normalizedMecanico.id || mecanico.id, !isActive)}
                  className={`text-white ${isActive ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} px-2 py-1 rounded`}
                  title={isActive ? 'Desactivar' : 'Activar'}
                >
                  {isActive ? <FaToggleOn /> : <FaToggleOff />}
                </button>
                
                <button
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded"
                  title="Eliminar"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
      
      {/* Diálogo de confirmación para eliminar */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => {
          onDelete(normalizedMecanico.id || mecanico.id);
          setIsDeleteDialogOpen(false);
        }}
        title="Eliminar mecánico"
        message={`¿Estás seguro de que deseas eliminar a ${normalizedMecanico.nombre || normalizedMecanico.Nombre}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        confirmVariant="danger"
        icon={<DeleteIcon />}
      />
    </>
  );
};

export default MecanicoItem;